import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './Auth';
import axios from 'axios';
import GradientButton from '../Elements/GradientButton';

const SignInScreen: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [logingIn, setLogingIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [openResetModal, setOpenResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetUsername, setResetUsername] = useState('');

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setLogingIn(true);

        signInWithEmailAndPassword(auth, username, password)
            .then((userCredential) => {
                navigate('/');
                setIsLoading(false);
                setLogingIn(false);
            })
            .catch((error) => {
                setIsLoading(false);
                setLogingIn(false);
                setErrorMessage('Invalid credentials...');
            });
    };

    const handlePasswordVisibilityToggle = () => {
        setShowPassword(!showPassword);
    };

    const handleForgotPasswordClick = () => {
        setOpenResetModal(true);
    };

    const handleResetPassword = () => {
        axios.post('/pool/auth/resetpassword', {
            email: resetEmail,
            username: resetUsername
        })
            .then(response => {
                const { status, message } = response.data;
                if (status) {
                    sendPasswordResetEmail(auth, resetEmail)
                        .then(() => {
                            setSuccessMessage("Password reset email sent.");
                            setOpenResetModal(false);
                            setResetEmail('');
                            setResetUsername('');
                        })
                        .catch(() => {
                            setErrorMessage("Failed to send password reset email.");
                        });
                } else {
                    setErrorMessage(message);
                }
            })
            .catch(() => {
                setErrorMessage('Failed to send reset email.');
            });
    };

    const handleCloseSnackbar = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
    };


    return (
        <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-[#FAF8F5] via-[#E5DDD0] to-[#D5C5A9] p-4 overflow-hidden">
            <div className="absolute top-20 left-10 w-64 h-64 bg-[#E5BC75]/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#8A540E]/10 rounded-full blur-3xl animate-pulse delay-700"></div>
            <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-[#D5C5A9]/30 rounded-full blur-2xl"></div>

            <div className="absolute top-10 right-20 w-16 h-16 border-4 border-[#E5BC75]/30 rotate-45 animate-spin-slow"></div>
            <div className="absolute bottom-32 left-16 w-12 h-12 bg-[#8A540E]/20 rounded-lg rotate-12"></div>
            <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-[#5E482C]/20 rounded-full"></div>

            <div className="relative bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-2xl w-full max-w-md border-2 border-[#D5C5A9]/50 z-10">
                <div className="flex justify-center ">
                    <div className="relative mb-3">
                        <img
                            src="/assets/cashlabblk.png"
                            alt="Logo"
                            className="h-8 object-contain"
                        />
                    </div>
                </div>
                <div className="border-t border-[#D5C5A9]/50 mb-6"></div>

                {!openResetModal && (
                    <>
                        <h2 className="text-xl font-bold text-center text-[#412F19] mb-2 tracking-wide">SIGN IN</h2>
                        <p className="text-center text-[#5E482C] text-sm mb-8">Welcome back! Please enter your details</p>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-5">
                                <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="email">
                                    Email *
                                </label>
                                <input
                                    className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                    type="email"
                                    id="email"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    placeholder="Enter Email"
                                />
                            </div>
                            <div className="mb-5 relative">
                                <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="password">
                                    Password *
                                </label>
                                <div className="relative">
                                    <input
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        id="hs-toggle-password"
                                        type={showPassword ? "text" : "password"}
                                        className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                        placeholder="Enter password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 end-0 flex items-center px-3 cursor-pointer text-[#E5BC75] hover:text-[#8A540E] transition-colors rounded-e-lg focus:outline-none"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <svg
                                            className="shrink-0 w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            viewBox="0 0 24 24"
                                        >
                                            {showPassword ? (
                                                <>
                                                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </>
                                            ) : (
                                                <>
                                                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                                    <line x1="2" y1="2" x2="22" y2="22" />
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between mb-6 text-sm">
                                <label className="flex items-center text-[#5E482C] cursor-pointer hover:text-[#412F19] transition-colors">
                                    <input type="checkbox" className="mr-2 w-4 h-4 accent-[#8A540E] cursor-pointer rounded" />
                                    Remember me
                                </label>
                                <button
                                    type="button"
                                    className="text-[#E5BC75] hover:text-[#8A540E] font-medium transition-colors"
                                    onClick={handleForgotPasswordClick}
                                >
                                    Forgot Password?
                                </button>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full p-3 rounded-lg font-bold text-white bg-gradient-to-r from-[#E5BC75] via-[#8A540E] to-[#8A540E] hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? "SIGNING IN..." : "SIGN IN"}
                            </button>
                        </form>

                        <div className="flex items-center gap-4 my-6">
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D5C5A9] to-transparent"></div>
                            <span className="text-[#5E482C] text-xs font-medium">OR</span>
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D5C5A9] to-transparent"></div>
                        </div>

                        <div className="text-center">
                            <p className="text-[#5E482C] text-sm">
                                {"Don't have an account? "}
                                <span
                                    className="font-bold cursor-pointer text-[#E5BC75] hover:text-[#8A540E] transition-colors"
                                    onClick={() => navigate('/signup')}
                                >
                                    Sign up →
                                </span>
                            </p>
                        </div>
                    </>
                )}
                {openResetModal && (
                    <>
                        <h2 className="text-2xl font-bold text-center text-[#412F19] mb-6">Reset Password</h2>
                        <div className="mb-5">
                            <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="resetUsername">
                                Username
                            </label>
                            <input
                                type="text"
                                id="resetUsername"
                                value={resetUsername}
                                onChange={(e) => setResetUsername(e.target.value)}
                                className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                placeholder="Enter your username"
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="resetEmail">
                                Email
                            </label>
                            <input
                                type="email"
                                id="resetEmail"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                placeholder="Enter your email"
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setOpenResetModal(false)}
                                className="px-6 py-2.5 bg-[#D5C5A9] text-[#412F19] rounded-lg hover:bg-[#D5C5A9]/80 focus:outline-none font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResetPassword}
                                className="px-6 py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-[#E5BC75] via-[#8A540E] to-[#8A540E] hover:shadow-lg transition-all duration-300"
                            >
                                Reset Password
                            </button>
                        </div>
                    </>
                )}
            </div>

            {(errorMessage || successMessage) && (
                <div
                    className={`fixed bottom-5 left-5 p-4 rounded-lg shadow-xl w-full max-w-sm flex items-center gap-3 z-50 ${successMessage
                        ? "bg-[#E5BC75] border-2 border-[#8A540E] text-[#412F19]"
                        : "bg-red-50 border-2 border-red-500 text-red-700"
                        }`}
                    role="alert"
                >
                    <div className="flex-1 text-sm font-medium">{successMessage || errorMessage}</div>
                    <button className="text-current hover:opacity-70 transition-opacity" onClick={handleCloseSnackbar}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-5 h-5"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SignInScreen;
